import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  identityChanges: "Identity changes",
  identityChangesDescription: "Each one voids Verified ID and screening",
  latest: (n: number) => `Latest ${n}`,
  openIdentityChanges: "Open identity changes",
  noIdentityChanges: "No identity changes yet.",
  changed: (field: string) => `${field} changed`,
  fieldName: "Name",
  fieldSsn: "National ID",
  fieldCountry: "Country",
  fieldDateOfBirth: "Date of birth",
  fieldOrgId: "Org number",
  sourceClientEditor: "client editor",
  sourceTrapetsLookup: "PEP/Sanction form",
  sourceSumsubInternal: "ID document (advisor)",
  sourceSumsubPublic: "ID document (client)",
  sourceEidLogin: "eID login (client)",
  sourceFlowCompletion: "flow completion",
  sourcePublicApi: "public API",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    identityChanges: "Modifications d'identité",
    identityChangesDescription: "Chacune annule l'identité vérifiée et le filtrage",
    latest: (n) => `${n} dernières`,
    openIdentityChanges: "Ouvrir les modifications d'identité",
    noIdentityChanges: "Aucune modification d'identité pour le moment.",
    changed: (field) => `Modification : ${field}`,
    fieldName: "Nom",
    fieldSsn: "N° d'identification national",
    fieldCountry: "Pays",
    fieldDateOfBirth: "Date de naissance",
    fieldOrgId: "N° d'entreprise",
    sourceClientEditor: "éditeur client",
    sourceTrapetsLookup: "formulaire PEP/sanctions",
    sourceSumsubInternal: "pièce d'identité (conseiller)",
    sourceSumsubPublic: "pièce d'identité (client)",
    sourceEidLogin: "connexion eID (client)",
    sourceFlowCompletion: "parcours terminé",
    sourcePublicApi: "API publique",
  },
  es_ES: {
    identityChanges: "Cambios de identidad",
    identityChangesDescription: "Cada uno anula la identidad verificada y el screening",
    latest: (n) => `Últimos ${n}`,
    openIdentityChanges: "Abrir cambios de identidad",
    noIdentityChanges: "Aún no hay cambios de identidad.",
    changed: (field) => `Cambio: ${field}`,
    fieldName: "Nombre",
    fieldSsn: "N.º de identificación nacional",
    fieldCountry: "País",
    fieldDateOfBirth: "Fecha de nacimiento",
    fieldOrgId: "N.º de empresa",
    sourceClientEditor: "editor de clientes",
    sourceTrapetsLookup: "formulario PEP/sanciones",
    sourceSumsubInternal: "documento de identidad (asesor)",
    sourceSumsubPublic: "documento de identidad (cliente)",
    sourceEidLogin: "inicio de sesión eID (cliente)",
    sourceFlowCompletion: "flujo completado",
    sourcePublicApi: "API pública",
  },
  pt_BR: {
    identityChanges: "Alterações de identidade",
    identityChangesDescription: "Cada uma invalida a identidade verificada e a triagem",
    latest: (n) => `Últimas ${n}`,
    openIdentityChanges: "Abrir alterações de identidade",
    noIdentityChanges: "Nenhuma alteração de identidade ainda.",
    changed: (field) => `Alteração: ${field}`,
    fieldName: "Nome",
    fieldSsn: "Nº de identidade nacional",
    fieldCountry: "País",
    fieldDateOfBirth: "Data de nascimento",
    fieldOrgId: "Nº da empresa",
    sourceClientEditor: "editor de clientes",
    sourceTrapetsLookup: "formulário PEP/sanções",
    sourceSumsubInternal: "documento de identidade (assessor)",
    sourceSumsubPublic: "documento de identidade (cliente)",
    sourceEidLogin: "login eID (cliente)",
    sourceFlowCompletion: "conclusão do fluxo",
    sourcePublicApi: "API pública",
  },
  de_DE: {
    identityChanges: "Identitätsänderungen",
    identityChangesDescription:
      "Jede Änderung hebt die verifizierte Identität und das Screening auf",
    latest: (n) => `Letzte ${n}`,
    openIdentityChanges: "Identitätsänderungen öffnen",
    noIdentityChanges: "Noch keine Identitätsänderungen.",
    changed: (field) => `${field} geändert`,
    fieldName: "Name",
    fieldSsn: "Nationale Identifikationsnummer",
    fieldCountry: "Land",
    fieldDateOfBirth: "Geburtsdatum",
    fieldOrgId: "Organisationsnummer",
    sourceClientEditor: "Kundeneditor",
    sourceTrapetsLookup: "PEP-/Sanktionsformular",
    sourceSumsubInternal: "Ausweisdokument (Berater)",
    sourceSumsubPublic: "Ausweisdokument (Kunde)",
    sourceEidLogin: "eID-Anmeldung (Kunde)",
    sourceFlowCompletion: "Flow-Abschluss",
    sourcePublicApi: "öffentliche API",
  },
  it_IT: {
    identityChanges: "Modifiche dell'identità",
    identityChangesDescription: "Ognuna annulla l'identità verificata e lo screening",
    latest: (n) => `Ultime ${n}`,
    openIdentityChanges: "Apri modifiche dell'identità",
    noIdentityChanges: "Ancora nessuna modifica dell'identità.",
    changed: (field) => `Modifica: ${field}`,
    fieldName: "Nome",
    fieldSsn: "Codice identificativo nazionale",
    fieldCountry: "Paese",
    fieldDateOfBirth: "Data di nascita",
    fieldOrgId: "Numero di registro imprese",
    sourceClientEditor: "editor clienti",
    sourceTrapetsLookup: "modulo PEP/sanzioni",
    sourceSumsubInternal: "documento d'identità (consulente)",
    sourceSumsubPublic: "documento d'identità (cliente)",
    sourceEidLogin: "accesso eID (cliente)",
    sourceFlowCompletion: "completamento del flusso",
    sourcePublicApi: "API pubblica",
  },
  ar: {
    identityChanges: "تغييرات الهوية",
    identityChangesDescription: "كل تغيير يُلغي الهوية الموثّقة وفحص القوائم",
    latest: (n) => `أحدث ${n}`,
    openIdentityChanges: "فتح تغييرات الهوية",
    noIdentityChanges: "لا توجد تغييرات في الهوية بعد.",
    changed: (field) => `تم تغيير ${field}`,
    fieldName: "الاسم",
    fieldSsn: "رقم الهوية الوطنية",
    fieldCountry: "الدولة",
    fieldDateOfBirth: "تاريخ الميلاد",
    fieldOrgId: "رقم المنشأة",
    sourceClientEditor: "محرر العميل",
    sourceTrapetsLookup: "نموذج PEP/العقوبات",
    sourceSumsubInternal: "وثيقة الهوية (المستشار)",
    sourceSumsubPublic: "وثيقة الهوية (العميل)",
    sourceEidLogin: "تسجيل الدخول بالهوية الإلكترونية (العميل)",
    sourceFlowCompletion: "إكمال المسار",
    sourcePublicApi: "واجهة API العامة",
  },
  ja_JP: {
    identityChanges: "本人情報の変更",
    identityChangesDescription: "変更のたびに本人確認とスクリーニングが無効になります",
    latest: (n) => `最新 ${n} 件`,
    openIdentityChanges: "本人情報の変更を開く",
    noIdentityChanges: "本人情報の変更はまだありません。",
    changed: (field) => `${field}が変更されました`,
    fieldName: "氏名",
    fieldSsn: "国民識別番号",
    fieldCountry: "国",
    fieldDateOfBirth: "生年月日",
    fieldOrgId: "法人番号",
    sourceClientEditor: "顧客エディター",
    sourceTrapetsLookup: "PEP／制裁フォーム",
    sourceSumsubInternal: "本人確認書類（アドバイザー）",
    sourceSumsubPublic: "本人確認書類（顧客）",
    sourceEidLogin: "eID ログイン（顧客）",
    sourceFlowCompletion: "フロー完了",
    sourcePublicApi: "公開 API",
  },
  hi_IN: {
    identityChanges: "पहचान में बदलाव",
    identityChangesDescription: "हर बदलाव सत्यापित पहचान और स्क्रीनिंग को अमान्य कर देता है",
    latest: (n) => `नवीनतम ${n}`,
    openIdentityChanges: "पहचान में बदलाव खोलें",
    noIdentityChanges: "अभी पहचान में कोई बदलाव नहीं है।",
    changed: (field) => `${field} बदला गया`,
    fieldName: "नाम",
    fieldSsn: "राष्ट्रीय पहचान संख्या",
    fieldCountry: "देश",
    fieldDateOfBirth: "जन्म तिथि",
    fieldOrgId: "संगठन संख्या",
    sourceClientEditor: "क्लाइंट संपादक",
    sourceTrapetsLookup: "PEP/प्रतिबंध फ़ॉर्म",
    sourceSumsubInternal: "पहचान दस्तावेज़ (सलाहकार)",
    sourceSumsubPublic: "पहचान दस्तावेज़ (क्लाइंट)",
    sourceEidLogin: "eID लॉगिन (क्लाइंट)",
    sourceFlowCompletion: "फ़्लो पूर्ण होना",
    sourcePublicApi: "सार्वजनिक API",
  },
  zh_CN: {
    identityChanges: "身份信息变更",
    identityChangesDescription: "每次变更都会使已验证身份和筛查失效",
    latest: (n) => `最近 ${n} 条`,
    openIdentityChanges: "打开身份信息变更",
    noIdentityChanges: "暂无身份信息变更。",
    changed: (field) => `${field}已变更`,
    fieldName: "姓名",
    fieldSsn: "身份证号",
    fieldCountry: "国家",
    fieldDateOfBirth: "出生日期",
    fieldOrgId: "机构编号",
    sourceClientEditor: "客户编辑器",
    sourceTrapetsLookup: "PEP/制裁表单",
    sourceSumsubInternal: "身份证件（顾问）",
    sourceSumsubPublic: "身份证件（客户）",
    sourceEidLogin: "eID 登录（客户）",
    sourceFlowCompletion: "流程完成",
    sourcePublicApi: "公开 API",
  },
  zh_TW: {
    identityChanges: "身分資料變更",
    identityChangesDescription: "每次變更都會使已驗證身分與篩檢失效",
    latest: (n) => `最近 ${n} 筆`,
    openIdentityChanges: "開啟身分資料變更",
    noIdentityChanges: "尚無身分資料變更。",
    changed: (field) => `${field}已變更`,
    fieldName: "姓名",
    fieldSsn: "身分證字號",
    fieldCountry: "國家",
    fieldDateOfBirth: "出生日期",
    fieldOrgId: "機構編號",
    sourceClientEditor: "客戶編輯器",
    sourceTrapetsLookup: "PEP/制裁表單",
    sourceSumsubInternal: "身分證件（顧問）",
    sourceSumsubPublic: "身分證件（客戶）",
    sourceEidLogin: "eID 登入（客戶）",
    sourceFlowCompletion: "流程完成",
    sourcePublicApi: "公開 API",
  },
  nl_NL: {
    identityChanges: "Identiteitswijzigingen",
    identityChangesDescription:
      "Elke wijziging maakt de geverifieerde identiteit en screening ongeldig",
    latest: (n) => `Laatste ${n}`,
    openIdentityChanges: "Identiteitswijzigingen openen",
    noIdentityChanges: "Nog geen identiteitswijzigingen.",
    changed: (field) => `${field} gewijzigd`,
    fieldName: "Naam",
    fieldSsn: "Burgerservicenummer",
    fieldCountry: "Land",
    fieldDateOfBirth: "Geboortedatum",
    fieldOrgId: "KvK-nummer",
    sourceClientEditor: "klanteditor",
    sourceTrapetsLookup: "PEP-/sanctieformulier",
    sourceSumsubInternal: "identiteitsbewijs (adviseur)",
    sourceSumsubPublic: "identiteitsbewijs (klant)",
    sourceEidLogin: "eID-login (klant)",
    sourceFlowCompletion: "flow voltooid",
    sourcePublicApi: "openbare API",
  },
  nb_NO: {
    identityChanges: "Identitetsendringer",
    identityChangesDescription: "Hver endring opphever verifisert ID og screening",
    latest: (n) => `Siste ${n}`,
    openIdentityChanges: "Åpne identitetsendringer",
    noIdentityChanges: "Ingen identitetsendringer ennå.",
    changed: (field) => `${field} endret`,
    fieldName: "Navn",
    fieldSsn: "Fødselsnummer",
    fieldCountry: "Land",
    fieldDateOfBirth: "Fødselsdato",
    fieldOrgId: "Organisasjonsnummer",
    sourceClientEditor: "kunderedigering",
    sourceTrapetsLookup: "PEP/sanksjonsskjema",
    sourceSumsubInternal: "ID-dokument (rådgiver)",
    sourceSumsubPublic: "ID-dokument (kunde)",
    sourceEidLogin: "eID-innlogging (kunde)",
    sourceFlowCompletion: "fullført flyt",
    sourcePublicApi: "offentlig API",
  },
  sv_SE: {
    identityChanges: "Identitetsändringar",
    identityChangesDescription: "Varje ändring upphäver verifierat ID och screening",
    latest: (n) => `Senaste ${n}`,
    openIdentityChanges: "Öppna identitetsändringar",
    noIdentityChanges: "Inga identitetsändringar ännu.",
    changed: (field) => `${field} ändrades`,
    fieldName: "Namn",
    fieldSsn: "Personnummer",
    fieldCountry: "Land",
    fieldDateOfBirth: "Födelsedatum",
    fieldOrgId: "Organisationsnummer",
    sourceClientEditor: "kundredigering",
    sourceTrapetsLookup: "PEP-/sanktionsformulär",
    sourceSumsubInternal: "ID-handling (rådgivare)",
    sourceSumsubPublic: "ID-handling (kund)",
    sourceEidLogin: "e-legitimationsinloggning (kund)",
    sourceFlowCompletion: "slutfört flöde",
    sourcePublicApi: "publikt API",
  },
  fi_FI: {
    identityChanges: "Henkilötietomuutokset",
    identityChangesDescription: "Jokainen muutos mitätöi vahvistetun henkilöllisyyden ja seulonnan",
    latest: (n) => `Viimeisimmät ${n}`,
    openIdentityChanges: "Avaa henkilötietomuutokset",
    noIdentityChanges: "Ei vielä henkilötietomuutoksia.",
    changed: (field) => `${field} muutettu`,
    fieldName: "Nimi",
    fieldSsn: "Henkilötunnus",
    fieldCountry: "Maa",
    fieldDateOfBirth: "Syntymäaika",
    fieldOrgId: "Y-tunnus",
    sourceClientEditor: "asiakastietojen muokkaus",
    sourceTrapetsLookup: "PEP-/pakotelomake",
    sourceSumsubInternal: "henkilöllisyysasiakirja (neuvoja)",
    sourceSumsubPublic: "henkilöllisyysasiakirja (asiakas)",
    sourceEidLogin: "sähköinen tunnistautuminen (asiakas)",
    sourceFlowCompletion: "prosessin valmistuminen",
    sourcePublicApi: "julkinen API",
  },
  da_DK: {
    identityChanges: "Identitetsændringer",
    identityChangesDescription: "Hver ændring ophæver verificeret ID og screening",
    latest: (n) => `Seneste ${n}`,
    openIdentityChanges: "Åbn identitetsændringer",
    noIdentityChanges: "Ingen identitetsændringer endnu.",
    changed: (field) => `${field} ændret`,
    fieldName: "Navn",
    fieldSsn: "CPR-nummer",
    fieldCountry: "Land",
    fieldDateOfBirth: "Fødselsdato",
    fieldOrgId: "CVR-nummer",
    sourceClientEditor: "kunderedigering",
    sourceTrapetsLookup: "PEP-/sanktionsformular",
    sourceSumsubInternal: "ID-dokument (rådgiver)",
    sourceSumsubPublic: "ID-dokument (kunde)",
    sourceEidLogin: "eID-login (kunde)",
    sourceFlowCompletion: "gennemført forløb",
    sourcePublicApi: "offentligt API",
  },
  is_IS: {
    identityChanges: "Breytingar á auðkenni",
    identityChangesDescription: "Hver breyting ógildir staðfest auðkenni og skimun",
    latest: (n) => `Nýjustu ${n}`,
    openIdentityChanges: "Opna breytingar á auðkenni",
    noIdentityChanges: "Engar breytingar á auðkenni enn.",
    changed: (field) => `Breytt: ${field}`,
    fieldName: "Nafn",
    fieldSsn: "Kennitala",
    fieldCountry: "Land",
    fieldDateOfBirth: "Fæðingardagur",
    fieldOrgId: "Kennitala fyrirtækis",
    sourceClientEditor: "breyting viðskiptavinar",
    sourceTrapetsLookup: "PEP-/þvingunareyðublað",
    sourceSumsubInternal: "skilríki (ráðgjafi)",
    sourceSumsubPublic: "skilríki (viðskiptavinur)",
    sourceEidLogin: "innskráning með rafrænum skilríkjum (viðskiptavinur)",
    sourceFlowCompletion: "ferli lokið",
    sourcePublicApi: "opinbert API",
  },
};
